import { useState, useMemo, useCallback } from "react";
import {
  Container,
  Box,
  Title,
  Text,
  Stack,
  Modal,
  Button,
  Group,
  Badge,
  MultiSelect,
  Paper,
  TextInput,
  Table,
  Pagination,
  Tooltip,
  Loader,
  Center,
  ActionIcon,
  Divider,
} from "@mantine/core";
import { useDisclosure, useDebouncedValue } from "@mantine/hooks";
import { DatePickerInput, type DatesRangeValue } from "@mantine/dates";
import {
  IconSearch,
  IconFilter,
  IconRefresh,
  IconPrinter,
  IconAlertCircle,
} from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import {
  bridgeAuditService,
  type BridgeAuditOutcome,
  type BridgeAuditRow,
  type BridgeAuditEventType,
  type BridgeDeviceType,
} from "../../services/bridgeAudit.service";
import { normalizeDateOnlyQueryRange } from "../../services/dateOnlyQuery";
import { parseDateOnly, type DateOnlyString } from "../../utils/dateOnly";
import { LAB_TIME_ZONE } from "../../utils/labTime";

const ITEMS_PER_PAGE = 20;

interface AuditFilters {
  deviceTypes: BridgeDeviceType[];
  eventTypes: BridgeAuditEventType[];
  outcomes: BridgeAuditOutcome[];
  from: DateOnlyString | null;
  to: DateOnlyString | null;
}

const EMPTY_FILTERS: AuditFilters = {
  deviceTypes: [],
  eventTypes: [],
  outcomes: [],
  from: null,
  to: null,
};

const DEVICE_TYPE_OPTIONS: { value: BridgeDeviceType; label: string }[] = [
  { value: "sticker_printer", label: "Sticker Printer (DYMO)" },
  { value: "slide_printer", label: "Slide Printer (SlideMate)" },
  { value: "cassette_printer", label: "Cassette Printer (PrintMate)" },
  { value: "document_scanner", label: "Document Scanner" },
  { value: "stainer", label: "Stainer (NeoPATH Pro)" },
];

const EVENT_TYPE_OPTIONS: { value: BridgeAuditEventType; label: string }[] = [
  { value: "print", label: "Print" },
  { value: "reprint", label: "Reprint" },
  { value: "scan", label: "Scan" },
  { value: "test", label: "Test" },
  { value: "stain_send", label: "Stain Order Sent" },
  { value: "stain_status", label: "Stain Status" },
];

const OUTCOME_OPTIONS: { value: BridgeAuditOutcome; label: string }[] = [
  { value: "success", label: "Success" },
  { value: "pending", label: "Pending" },
  { value: "failed_bridge", label: "Failed (bridge)" },
  { value: "failed_device", label: "Failed (device)" },
];

const LAB_AUDIT_DATE_TIME_FORMATTER = new Intl.DateTimeFormat("en-US", {
  timeZone: LAB_TIME_ZONE,
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hourCycle: "h23",
});

const LAB_AUDIT_TIME_FORMATTER = new Intl.DateTimeFormat("en-US", {
  timeZone: LAB_TIME_ZONE,
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hourCycle: "h23",
});

function formatAuditInstant(
  value: string,
  formatter: Intl.DateTimeFormat
): string {
  const instant = new Date(value);
  if (Number.isNaN(instant.getTime())) return "—";
  return formatter.format(instant).replace(",", "");
}

function outcomeColor(outcome: BridgeAuditOutcome): string {
  switch (outcome) {
    case "success":
      return "green";
    case "pending":
      return "yellow";
    case "failed_bridge":
    case "failed_device":
      return "red";
    default:
      return "gray";
  }
}

function deviceTypeColor(deviceType: BridgeDeviceType): string {
  switch (deviceType) {
    case "sticker_printer":
      return "violet";
    case "slide_printer":
      return "blue";
    case "cassette_printer":
      return "teal";
    case "document_scanner":
      return "gray";
    case "stainer":
      return "grape";
    default:
      return "gray";
  }
}

export default function BridgeAuditQueryPage() {
  const [
    filterModalOpened,
    { open: openFilterModal, close: closeFilterModal },
  ] = useDisclosure(false);

  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [filters, setFilters] = useState<AuditFilters>(EMPTY_FILTERS);
  const [pendingFilters, setPendingFilters] =
    useState<AuditFilters>(EMPTY_FILTERS);
  const [dateRangeError, setDateRangeError] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  const queryParams = useMemo(
    () => ({
      limit: ITEMS_PER_PAGE,
      offset: (page - 1) * ITEMS_PER_PAGE,
      search: debouncedSearch || undefined,
      deviceType: filters.deviceTypes[0],
      eventType: filters.eventTypes[0],
      outcome: filters.outcomes[0],
      from: filters.from ?? undefined,
      to: filters.to ?? undefined,
    }),
    [page, debouncedSearch, filters]
  );

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ["bridgeAudit", queryParams],
    queryFn: () => bridgeAuditService.list(queryParams),
    retry: false,
  });

  const rows: BridgeAuditRow[] = useMemo(
    () => (Array.isArray(data?.rows) ? data!.rows : []),
    [data]
  );

  const total = useMemo(() => data?.total ?? 0, [data?.total]);
  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(total / ITEMS_PER_PAGE)),
    [total]
  );

  const hasActiveFilters = useMemo(
    () =>
      filters.deviceTypes.length > 0 ||
      filters.eventTypes.length > 0 ||
      filters.outcomes.length > 0 ||
      !!filters.from ||
      !!filters.to,
    [filters]
  );

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearch(e.currentTarget.value);
      setPage(1);
    },
    []
  );

  const handleOpenFilters = useCallback(() => {
    setPendingFilters(filters);
    setDateRangeError(null);
    openFilterModal();
  }, [filters, openFilterModal]);

  const handleApplyFilters = useCallback(() => {
    if (dateRangeError) return;
    try {
      normalizeDateOnlyQueryRange(
        {
          from: pendingFilters.from ?? undefined,
          to: pendingFilters.to ?? undefined,
        },
        { from: "From date", to: "To date" }
      );
      setFilters(pendingFilters);
      setDateRangeError(null);
      setPage(1);
      closeFilterModal();
    } catch {
      setDateRangeError("From date must be before or equal to To date.");
    }
  }, [dateRangeError, pendingFilters, closeFilterModal]);

  const handleResetPendingFilters = useCallback(() => {
    setPendingFilters(EMPTY_FILTERS);
    setDateRangeError(null);
  }, []);

  const handleClearAllFilters = useCallback(() => {
    setFilters(EMPTY_FILTERS);
    setDateRangeError(null);
    setPage(1);
  }, []);

  const handleRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  const handlePageChange = useCallback((value: number) => {
    setPage(value);
  }, []);

  const handleDeviceTypesChange = useCallback((values: string[]) => {
    setPendingFilters((current) => ({
      ...current,
      deviceTypes: values as BridgeDeviceType[],
    }));
  }, []);

  const handleEventTypesChange = useCallback((values: string[]) => {
    setPendingFilters((current) => ({
      ...current,
      eventTypes: values as BridgeAuditEventType[],
    }));
  }, []);

  const handleOutcomesChange = useCallback((values: string[]) => {
    setPendingFilters((current) => ({
      ...current,
      outcomes: values as BridgeAuditOutcome[],
    }));
  }, []);

  const handleDateRangeChange = useCallback((value: DatesRangeValue) => {
    const from = value[0] === null ? null : parseDateOnly(value[0]);
    const to = value[1] === null ? null : parseDateOnly(value[1]);
    if ((value[0] !== null && !from) || (value[1] !== null && !to)) {
      setDateRangeError("Select valid calendar dates.");
      return;
    }
    setPendingFilters((current) => ({
      ...current,
      from: from ?? null,
      to: to ?? null,
    }));
    setDateRangeError(null);
  }, []);

  const rowViewModels = useMemo(
    () =>
      rows.map((row) => ({
        row,
        itemLabel: row.itemType
          ? `${row.itemType}${row.itemId ? `:${row.itemId.slice(0, 8)}` : ""}`
          : "—",
        startedAtLabel: formatAuditInstant(
          row.startedAt,
          LAB_AUDIT_DATE_TIME_FORMATTER
        ),
        completedAtLabel:
          row.completedAt && row.completedAt !== row.startedAt
            ? formatAuditInstant(row.completedAt, LAB_AUDIT_TIME_FORMATTER)
            : null,
      })),
    [rows]
  );

  return (
    <Container fluid py="xl">
      <Box style={{ maxWidth: "1600px", margin: "0 auto" }}>
        <Stack gap="lg">
          {/* Header */}
          <Group justify="space-between" align="flex-start">
            <div>
              <Title order={2}>Bridge Device Audit Log</Title>
              <Text size="sm" c="dimmed" mt="xs">
                Every print, reprint, scan, and test dispatched to a bridge
                device, with the user, workstation, device, and case context.
                Filter by device type, event type, outcome, or date.
              </Text>
            </div>
          </Group>

          {/* Search + Filters */}
          <Group align="stretch" gap="md" grow>
            <Paper p="md" withBorder style={{ flex: 2 }}>
              <Group gap="xs" wrap="nowrap">
                <IconSearch
                  size={16}
                  style={{ color: "var(--mantine-color-dimmed)" }}
                />
                <Text size="sm" fw={500} c="dimmed">
                  Search
                </Text>
                <TextInput
                  placeholder="Case number, item id, or request id…"
                  value={search}
                  onChange={handleSearchChange}
                  style={{ flex: 1 }}
                />
              </Group>
            </Paper>

            <Paper p="md" withBorder style={{ flex: 1 }}>
              <Group gap="xs" wrap="wrap">
                <IconFilter
                  size={16}
                  style={{ color: "var(--mantine-color-dimmed)" }}
                />
                <Tooltip label="Refresh">
                  <ActionIcon
                    variant="subtle"
                    onClick={handleRefresh}
                    loading={isFetching}
                  >
                    <IconRefresh size={16} />
                  </ActionIcon>
                </Tooltip>
                <Button
                  variant={hasActiveFilters ? "filled" : "subtle"}
                  size="compact-sm"
                  onClick={handleOpenFilters}
                  leftSection={<IconFilter size={14} />}
                >
                  Filters
                </Button>
                {hasActiveFilters && (
                  <>
                    {filters.deviceTypes.length > 0 && (
                      <Badge variant="light" color="violet">
                        {filters.deviceTypes.length} Device
                        {filters.deviceTypes.length === 1 ? "" : "s"}
                      </Badge>
                    )}
                    {filters.eventTypes.length > 0 && (
                      <Badge variant="light" color="blue">
                        {filters.eventTypes.length} Event
                        {filters.eventTypes.length === 1 ? "" : "s"}
                      </Badge>
                    )}
                    {filters.outcomes.length > 0 && (
                      <Badge variant="light" color="teal">
                        {filters.outcomes.length} Outcome
                        {filters.outcomes.length === 1 ? "" : "s"}
                      </Badge>
                    )}
                    {(filters.from || filters.to) && (
                      <Badge variant="light" color="orange">
                        Date range
                      </Badge>
                    )}
                    <Button
                      variant="subtle"
                      size="compact-xs"
                      color="gray"
                      onClick={handleClearAllFilters}
                    >
                      Clear all
                    </Button>
                  </>
                )}
                <Text size="sm" c="dimmed" ml="auto">
                  {total} event{total === 1 ? "" : "s"}
                </Text>
              </Group>
            </Paper>
          </Group>

          {/* Table */}
          <Paper p="md" withBorder>
            <Stack gap="md">
              <Group justify="space-between">
                <Text fw={500} size="lg">
                  Events ({total})
                </Text>
                {totalPages > 1 && (
                  <Group gap="md">
                    <Text size="sm" c="dimmed">
                      Showing {(page - 1) * ITEMS_PER_PAGE + 1}–
                      {Math.min(page * ITEMS_PER_PAGE, total)} of {total}
                    </Text>
                    <Pagination
                      total={totalPages}
                      value={page}
                      onChange={handlePageChange}
                      size="sm"
                    />
                  </Group>
                )}
              </Group>

              {isLoading ? (
                <Center p="xl">
                  <Loader />
                </Center>
              ) : rows.length === 0 ? (
                <Text c="dimmed" ta="center" py="xl">
                  No audit events found
                </Text>
              ) : (
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>When</Table.Th>
                      <Table.Th>Device type</Table.Th>
                      <Table.Th>Event</Table.Th>
                      <Table.Th>Outcome</Table.Th>
                      <Table.Th>User</Table.Th>
                      <Table.Th>Case #</Table.Th>
                      <Table.Th>Item</Table.Th>
                      <Table.Th>Device</Table.Th>
                      <Table.Th>Workstation</Table.Th>
                      <Table.Th>Failure</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {rowViewModels.map(
                      ({
                        row,
                        itemLabel,
                        startedAtLabel,
                        completedAtLabel,
                      }) => (
                        <Table.Tr key={row.id}>
                          <Table.Td>
                            <Text size="xs">{startedAtLabel}</Text>
                            {completedAtLabel && (
                              <Text size="xs" c="dimmed">
                                done {completedAtLabel}
                              </Text>
                            )}
                          </Table.Td>
                          <Table.Td>
                            <Badge
                              size="sm"
                              color={deviceTypeColor(row.deviceType)}
                              variant="light"
                            >
                              {row.deviceType.replace("_", " ")}
                            </Badge>
                          </Table.Td>
                          <Table.Td>
                            <Group gap={4}>
                              <Badge size="sm" variant="outline">
                                {row.eventType}
                              </Badge>
                              {row.isReprint && (
                                <Badge size="sm" color="orange" variant="light">
                                  reprint
                                </Badge>
                              )}
                            </Group>
                          </Table.Td>
                          <Table.Td>
                            <Badge
                              size="sm"
                              color={outcomeColor(row.outcome)}
                              variant="light"
                            >
                              {row.outcome}
                            </Badge>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm" fw={500}>
                              {row.userName ?? "—"}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm">{row.caseNumber ?? "—"}</Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="xs" c="dimmed">
                              {itemLabel}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm">{row.deviceName ?? "—"}</Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm">{row.workstationName ?? "—"}</Text>
                          </Table.Td>
                          <Table.Td>
                            {row.failureReason ? (
                              <Tooltip
                                label={row.failureReason}
                                multiline
                                w={280}
                              >
                                <Text size="xs" c="red" lineClamp={1}>
                                  {row.failureReason}
                                </Text>
                              </Tooltip>
                            ) : (
                              <Text size="xs" c="dimmed">
                                —
                              </Text>
                            )}
                          </Table.Td>
                        </Table.Tr>
                      )
                    )}
                  </Table.Tbody>
                </Table>
              )}
            </Stack>
          </Paper>
        </Stack>

        {/* Filter Modal — pending-filters pattern */}
        <Modal.Root
          opened={filterModalOpened}
          onClose={closeFilterModal}
          size="md"
          centered
          closeOnClickOutside={false}
          styles={{
            content: {
              display: "flex",
              flexDirection: "column",
              maxHeight: "85vh",
            },
          }}
        >
          <Modal.Overlay />
          <Modal.Content>
            <Modal.Header>
              <Modal.Title>
                <Group gap="xs">
                  <IconPrinter size={18} />
                  <Text fw={600}>Filter audit events</Text>
                </Group>
              </Modal.Title>
              <Modal.CloseButton />
            </Modal.Header>

            <Modal.Body style={{ flex: 1, overflow: "auto" }}>
              <Stack gap="md">
                <MultiSelect
                  label="Device type"
                  placeholder="Any device"
                  data={DEVICE_TYPE_OPTIONS}
                  value={pendingFilters.deviceTypes}
                  onChange={handleDeviceTypesChange}
                  clearable
                  searchable
                />
                <MultiSelect
                  label="Event type"
                  placeholder="Any event"
                  data={EVENT_TYPE_OPTIONS}
                  value={pendingFilters.eventTypes}
                  onChange={handleEventTypesChange}
                  clearable
                />
                <MultiSelect
                  label="Outcome"
                  placeholder="Any outcome"
                  data={OUTCOME_OPTIONS}
                  value={pendingFilters.outcomes}
                  onChange={handleOutcomesChange}
                  clearable
                />
                <DatePickerInput
                  type="range"
                  label="Date range"
                  placeholder="Any date"
                  value={[pendingFilters.from, pendingFilters.to]}
                  onChange={handleDateRangeChange}
                  error={dateRangeError}
                  clearable
                />
                {(pendingFilters.deviceTypes.length > 1 ||
                  pendingFilters.eventTypes.length > 1 ||
                  pendingFilters.outcomes.length > 1) && (
                  <Group gap="xs" wrap="nowrap">
                    <IconAlertCircle
                      size={14}
                      style={{ color: "var(--mantine-color-yellow-7)" }}
                    />
                    <Text size="xs" c="dimmed">
                      Multi-select returns rows matching the first value of each
                      filter (server supports one of each today).
                    </Text>
                  </Group>
                )}
              </Stack>
            </Modal.Body>

            <Divider />
            <Group justify="space-between" p="md">
              <Button variant="default" onClick={closeFilterModal}>
                Cancel
              </Button>
              <Group gap="xs">
                <Button
                  variant="subtle"
                  color="gray"
                  onClick={handleResetPendingFilters}
                >
                  Reset
                </Button>
                <Button onClick={handleApplyFilters}>Apply filters</Button>
              </Group>
            </Group>
          </Modal.Content>
        </Modal.Root>
      </Box>
    </Container>
  );
}
